import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  MagnifyingGlass, 
  ArrowsClockwise, 
  Copy,
  CheckCircle,
  ListChecks,
  Article,
  Tag
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShareResultButton } from '../components/ShareResultButton';
import { TextHistoryPanel } from '../components/TextHistoryPanel';
import { usePersistedInput, usePersistedOutput } from '../hooks/usePersistedState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const countries = ['United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'India', 'Global'];

export const SEOPage = () => {
  const [niche, setNiche] = usePersistedInput('seo', 'niche', '');
  const [productService, setProductService] = usePersistedInput('seo', 'productService', '');
  const [pageGoal, setPageGoal] = usePersistedInput('seo', 'pageGoal', '');
  const [targetCustomer, setTargetCustomer] = usePersistedInput('seo', 'targetCustomer', '');
  const [country, setCountry] = usePersistedInput('seo', 'country', 'United States');
  const [loading, setLoading] = useState(false);
  const [seoData, setSeoData] = usePersistedOutput('seo', null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('create');

  const handleHistoryReload = (item) => {
    const inputs = item.inputs || {};
    if (inputs.niche) setNiche(inputs.niche);
    if (inputs.product_service) setProductService(inputs.product_service);
    if (inputs.page_goal) setPageGoal(inputs.page_goal);
    if (inputs.target_customer) setTargetCustomer(inputs.target_customer);
    if (inputs.country) setCountry(inputs.country);
    const output = item.output || {};
    if (output.seo) setSeoData(output.seo);
    setTab('create');
  };

  useEffect(() => {
    const prefill = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/user/profile`);
        const bp = data.brand_profile;
        if (bp) {
          if (bp.industry) setNiche(prev => prev || bp.industry);
          if (bp.description) setProductService(prev => prev || bp.description);
          if (bp.target_audience) setTargetCustomer(prev => prev || bp.target_audience);
        }
      } catch (e) { /* ignore */ }
    };
    prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateSEO = async () => {
    if (!niche || !productService || !pageGoal || !targetCustomer) {
      setError('Please fill in all fields');
      return;
    }
    setError('');
    setLoading(true);
    setSeoData(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/seo/generate`, {
        niche,
        product_service: productService,
        page_goal: pageGoal,
        target_customer: targetCustomer,
        country
      });
      
      setSeoData(data.seo);
    } catch (err) {
      console.error('Failed to generate SEO:', err);
      setError('Generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <DashboardLayout>
      <div data-testid="seo-page" className="space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <MagnifyingGlass size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">SEO Assistant</h1>
              <p className="text-zinc-400 text-sm">Improve your search visibility</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: tab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${tab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: tab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Generate' : 'History'}
              </button>
            ))}
          </div>
        </motion.div>

        {tab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-1"
          >
            <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5 sticky top-24">
              <h2 className="font-medium font-['Instrument_Serif',serif]">SEO Settings</h2>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Niche / Industry</label>
                <input
                  type="text"
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  data-testid="niche-input"
                  placeholder="e.g., Organic skincare"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Product / Service</label>
                <input
                  type="text"
                  value={productService}
                  onChange={(e) => setProductService(e.target.value)}
                  data-testid="product-input"
                  placeholder="e.g., Natural face serums"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Page Goal</label>
                <input
                  type="text"
                  value={pageGoal}
                  onChange={(e) => setPageGoal(e.target.value)}
                  data-testid="goal-input"
                  placeholder="e.g., Drive product purchases"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Target Customer</label>
                <input
                  type="text"
                  value={targetCustomer}
                  onChange={(e) => setTargetCustomer(e.target.value)}
                  data-testid="customer-input"
                  placeholder="e.g., Women 25-45 interested in clean beauty"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Target Market</label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger data-testid="country-select" className="w-full bg-zinc-900 border-white/10 text-white">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {countries.map((c) => (
                      <SelectItem key={c} value={c} className="text-white hover:bg-white/10">
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <button
                onClick={generateSEO}
                disabled={loading || !niche || !productService || !pageGoal || !targetCustomer}
                data-testid="generate-seo-btn"
                className="w-full bg-white text-black hover:bg-zinc-200 transition-all duration-200 rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <ArrowsClockwise size={18} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <MagnifyingGlass size={18} />
                    Generate SEO Plan
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            {!seoData ? (
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                <div className="text-center py-16">
                  <MagnifyingGlass size={48} className="mx-auto mb-4 text-zinc-700" />
                  <h3 className="text-lg font-medium mb-2">No SEO data generated yet</h3>
                  <p className="text-sm">Fill in your details and generate your SEO plan</p>
                </div>
              </div>
            ) : (
              <>
                {/* Keywords */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Tag size={20} className="text-blue-400" />
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Target Keywords</h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShareResultButton
                        title="SEO Keywords & Strategy"
                        content={`Top keywords: ${seoData.keywords?.slice(0, 5).join(', ') || ''}`}
                        type="SEO Strategy"
                        variant="icon"
                      />
                      <button
                        onClick={() => copyToClipboard(seoData.keywords?.join(', ') || '', 'keywords')}
                        className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                      >
                        {copiedKey === 'keywords' ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {seoData.keywords?.map((keyword, index) => (
                      <span key={index} className="px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-300 text-sm border border-blue-500/20">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Meta Titles */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Meta Title Suggestions</h2>
                  <div className="space-y-3">
                    {seoData.meta_titles?.map((title, index) => (
                      <div key={index} className="flex items-center justify-between gap-4 p-3 bg-zinc-800/50 rounded-xl">
                        <span className="text-zinc-200 text-sm">{title}</span>
                        <button
                          onClick={() => copyToClipboard(title, `title-${index}`)}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {copiedKey === `title-${index}` ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Meta Descriptions */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Meta Description Suggestions</h2>
                  <div className="space-y-3">
                    {seoData.meta_descriptions?.map((desc, index) => (
                      <div key={index} className="flex items-start justify-between gap-4 p-3 bg-zinc-800/50 rounded-xl">
                        <span className="text-zinc-300 text-sm">{desc}</span>
                        <button
                          onClick={() => copyToClipboard(desc, `desc-${index}`)}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {copiedKey === `desc-${index}` ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SEO Checklist */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <ListChecks size={20} className="text-green-400" />
                    <h2 className="font-medium font-['Instrument_Serif',serif]">SEO Action Checklist</h2>
                  </div>
                  <div className="space-y-2">
                    {seoData.checklist?.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-2">
                        <div className="w-5 h-5 rounded border border-zinc-600" />
                        <span className="text-zinc-300 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Blog Topics */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Article size={20} className="text-purple-400" />
                    <h2 className="font-medium font-['Instrument_Serif',serif]">Blog Topic Suggestions</h2>
                  </div>
                  <div className="space-y-3">
                    {seoData.blog_topics?.map((topic, index) => (
                      <div key={index} className="flex items-center justify-between gap-4 p-3 bg-zinc-800/50 rounded-xl">
                        <span className="text-zinc-200 text-sm">{topic}</span>
                        <button
                          onClick={() => copyToClipboard(topic, `topic-${index}`)}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {copiedKey === `topic-${index}` ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Regenerate */}
                <button
                  onClick={generateSEO}
                  disabled={loading}
                  className="w-full py-2.5 text-center rounded-full border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
                  Regenerate SEO Plan
                </button>
              </>
            )}
          </motion.div>
        </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div className="rounded-[14px] p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">SEO History</h2>
            <TextHistoryPanel toolType="seo" onReload={handleHistoryReload} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SEOPage;
